import React from 'react';

/**
 * The radial fade every section illustration needs. The artwork is flat vector
 * on solid black, so dropped in raw it cuts a black square out of the page
 * background. `mix-blend-screen` would be the cleaner fix but the sections sit
 * inside `z-30` wrappers — their own stacking contexts — so the blend never
 * reaches the backdrop. Same reasoning as the hero banner in `App.js`.
 */
const ILLUSTRATION_MASK = 'radial-gradient(ellipse 72% 72% at 50% 50%, #000 55%, transparent 100%)';

/**
 * Eyebrow + h2 + optional intro, the heading block every content section on the
 * page shares. Kept as one component so the heading hierarchy asserted in
 * App.test.js can't drift section by section.
 *
 * `image` is an optional decorative illustration shown beside the copy. It
 * carries no meaning the heading doesn't already state, so it stays
 * `alt=""`/`aria-hidden` — a screen reader announcing "stablecoin coin" after
 * the section title adds nothing. On phones it drops below the copy: the copy
 * is what the visitor came for and the illustration should never push it down.
 */
const SectionHeading = ({ eyebrow, title, intro, centered = false, image = null }) => {
  const copy = (
    <div className={centered ? 'text-center' : ''}>
      <div className="text-main font-grotesk text-[25px] uppercase font-light mb-2">{eyebrow}</div>
      <h2 className="text-[35px] sm3:text-[45px] font-grotesk font-bold leading-[105%] text-white uppercase">
        {title}
      </h2>
      {intro ? (
        <p className={`text-main-gray mt-4 max-w-[720px] ${centered ? 'mx-auto' : ''}`}>{intro}</p>
      ) : null}
    </div>
  );

  if (!image) {
    return <div className="mb-10">{copy}</div>;
  }

  return (
    <div className="mb-10 flex flex-col-reverse md2:flex-row md2:items-center gap-4 md2:gap-10">
      <div className="flex-1 min-w-0">{copy}</div>
      <img
        src={image}
        alt=""
        aria-hidden="true"
        draggable="false"
        className="w-[150px] sm3:w-[190px] md2:w-[230px] lg:w-[260px] shrink-0 self-center"
        style={{ maskImage: ILLUSTRATION_MASK, WebkitMaskImage: ILLUSTRATION_MASK }}
      />
    </div>
  );
};

export default SectionHeading;
